import React, { FC, useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import MobileNavToggle from './MobileNavToggle';
import './use-case-editor.scss';

// ─── Types ───────────────────────────────────────────────────────────────────

interface UseCaseExpectation {
  id?: string;
  title: string;
  description: string;
  sortOrder: number;
  active: boolean;
}

interface UseCaseManager {
  name: string;
  active: boolean;
}

interface UseCase {
  id: string;
  slug: string;
  title: string;
  summary: string;
  description: string;
  limitations: string | null;
  recommendedModes: string[];
  active: boolean;
  createdAt: string;
  updatedAt: string;
  managers: UseCaseManager[];
  expectations: UseCaseExpectation[];
}

interface UseCaseSummary {
  id: string;
  slug: string;
  title: string;
  summary: string;
  active: boolean;
  managers: string[];
  expectationCount: number;
}

const MANAGER_OPTIONS = [
  'Personal Assistant',
  'Sales Assistant',
  'System Assistant',
  'Language Teacher Assistant',
  'Blank Assistant',
];

// ─── Sub-components ──────────────────────────────────────────────────────────

const ExpectationRow: FC<{
  expectation: UseCaseExpectation;
  index: number;
  onChange: (index: number, field: string, value: string | number) => void;
  onRemove: (index: number) => void;
}> = ({ expectation, index, onChange, onRemove }) => (
  <div className='expectation-row'>
    <div className='expectation-fields'>
      <input
        type='text'
        placeholder='Title'
        value={expectation.title}
        onChange={(e) => onChange(index, 'title', e.target.value)}
      />
      <textarea
        placeholder='Description'
        value={expectation.description}
        onChange={(e) => onChange(index, 'description', e.target.value)}
        rows={2}
      />
    </div>
    <button
      className='remove-btn'
      onClick={() => onRemove(index)}
      title='Remove'
    >
      &times;
    </button>
  </div>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const UseCaseEditor: FC = () => {
  const { getAccessTokenSilently } = useAuth0();

  // Access check
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);

  // List view
  const [useCases, setUseCases] = useState<UseCaseSummary[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);

  // Detail / edit view
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [detail, setDetail] = useState<UseCase | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Edit form state
  const [editSlug, setEditSlug] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editSummary, setEditSummary] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editLimitations, setEditLimitations] = useState('');
  const [editRecommendedModes, setEditRecommendedModes] = useState('');
  const [editManagers, setEditManagers] = useState<string[]>([]);
  const [editExpectations, setEditExpectations] = useState<
    UseCaseExpectation[]
  >([]);
  const [editActive, setEditActive] = useState(true);

  // UX
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [isNewMode, setIsNewMode] = useState(false);

  // ─── Helpers ─────────────────────────────────────────────────────────────

  const authHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { Authorization: `Bearer ${token}` };
  }, [getAccessTokenSilently]);

  const clearMessages = () => {
    setError(null);
    setSuccessMsg(null);
  };

  // ─── Fetch list ──────────────────────────────────────────────────────────

  const fetchList = useCallback(async () => {
    setListLoading(true);
    clearMessages();
    try {
      const headers = await authHeaders();
      const res = await api.get(
        `/agent-use-cases?includeInactive=${includeInactive}`,
        { headers },
      );
      if (res.data.success) {
        setUseCases(res.data.useCases);
        setHasAccess(true);
      }
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setHasAccess(false);
      } else {
        setError('Failed to load use cases');
      }
    } finally {
      setListLoading(false);
    }
  }, [authHeaders, includeInactive]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // ─── Fetch detail ────────────────────────────────────────────────────────

  const fetchDetail = useCallback(
    async (slug: string) => {
      setDetailLoading(true);
      clearMessages();
      try {
        const headers = await authHeaders();
        const res = await api.get(`/agent-use-cases/${slug}`, { headers });
        if (res.data.success) {
          const uc: UseCase = res.data.useCase;
          setDetail(uc);
          populateForm(uc);
        }
      } catch {
        setError('Failed to load use case details');
      } finally {
        setDetailLoading(false);
      }
    },
    [authHeaders],
  );

  useEffect(() => {
    if (selectedSlug && !isNewMode) {
      fetchDetail(selectedSlug);
    }
  }, [selectedSlug, isNewMode, fetchDetail]);

  // ─── Populate form from UseCase ──────────────────────────────────────────

  const populateForm = (uc: UseCase) => {
    setEditSlug(uc.slug);
    setEditTitle(uc.title);
    setEditSummary(uc.summary);
    setEditDescription(uc.description);
    setEditLimitations(uc.limitations || '');
    setEditRecommendedModes((uc.recommendedModes || []).join(', '));
    setEditManagers(uc.managers.filter((m) => m.active).map((m) => m.name));
    setEditExpectations(
      uc.expectations.map((e, i) => ({
        ...e,
        sortOrder: e.sortOrder ?? i,
      })),
    );
    setEditActive(uc.active);
  };

  // ─── New use case ────────────────────────────────────────────────────────

  const startNew = () => {
    setSelectedSlug(null);
    setDetail(null);
    setIsNewMode(true);
    setEditSlug('');
    setEditTitle('');
    setEditSummary('');
    setEditDescription('');
    setEditLimitations('');
    setEditRecommendedModes('');
    setEditManagers([]);
    setEditExpectations([]);
    setEditActive(true);
    clearMessages();
  };

  // ─── Select existing ────────────────────────────────────────────────────

  const selectUseCase = (slug: string) => {
    setIsNewMode(false);
    setSelectedSlug(slug);
    clearMessages();
  };

  // ─── Back to list ────────────────────────────────────────────────────────

  const backToList = () => {
    setSelectedSlug(null);
    setDetail(null);
    setIsNewMode(false);
    clearMessages();
    fetchList();
  };

  // ─── Expectation handlers ───────────────────────────────────────────────

  const handleExpectationChange = (
    index: number,
    field: string,
    value: string | number,
  ) => {
    setEditExpectations((prev) =>
      prev.map((e, i) => (i === index ? { ...e, [field]: value } : e)),
    );
  };

  const addExpectation = () => {
    setEditExpectations((prev) => [
      ...prev,
      { title: '', description: '', sortOrder: prev.length, active: true },
    ]);
  };

  const removeExpectation = (index: number) => {
    setEditExpectations((prev) => prev.filter((_, i) => i !== index));
  };

  // ─── Manager toggle ─────────────────────────────────────────────────────

  const toggleManager = (name: string) => {
    setEditManagers((prev) =>
      prev.includes(name) ? prev.filter((m) => m !== name) : [...prev, name],
    );
  };

  // ─── Save ────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    clearMessages();
    if (!editSlug || !editTitle || !editSummary || !editDescription) {
      setError('Slug, title, summary, and description are required.');
      return;
    }
    if (editManagers.length === 0) {
      setError('At least one manager must be selected.');
      return;
    }

    setSaving(true);
    try {
      const headers = await authHeaders();
      const modesArray = editRecommendedModes
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      const body = {
        slug: editSlug,
        title: editTitle,
        summary: editSummary,
        description: editDescription,
        limitations: editLimitations || null,
        recommendedModes: modesArray,
        active: editActive,
        managers: editManagers,
        expectations: editExpectations
          .filter((e) => e.title && e.description)
          .map((e, i) => ({
            ...(e.id ? { id: e.id } : {}),
            title: e.title,
            description: e.description,
            sortOrder: i,
            active: e.active !== false,
          })),
      };

      if (isNewMode) {
        const res = await api.post('/agent-use-cases', body, { headers });
        if (res.data.success) {
          setSuccessMsg(`Created "${editTitle}"`);
          setIsNewMode(false);
          setSelectedSlug(editSlug);
          fetchDetail(editSlug);
        }
      } else {
        const res = await api.put(`/agent-use-cases/${selectedSlug}`, body, {
          headers,
        });
        if (res.data.success) {
          setSuccessMsg(`Saved "${editTitle}"`);
          fetchDetail(editSlug);
        }
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete (soft) ──────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!selectedSlug) return;
    if (!window.confirm(`Deactivate "${editTitle}"?`)) return;

    clearMessages();
    setSaving(true);
    try {
      const headers = await authHeaders();
      const res = await api.delete(`/agent-use-cases/${selectedSlug}`, {
        headers,
      });
      if (res.data.success) {
        setSuccessMsg(res.data.message);
        setTimeout(backToList, 800);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  // ─── Render: access check ───────────────────────────────────────────────

  if (hasAccess === false) {
    return (
      <div className='use-case-editor'>
        <div className='access-denied'>Access denied.</div>
      </div>
    );
  }

  // ─── Render: list view ──────────────────────────────────────────────────

  if (!selectedSlug && !isNewMode) {
    return (
      <div className='use-case-editor'>
        <MobileNavToggle />
        <div className='header'>
          <h2>Use Cases</h2>
          <div className='header-actions'>
            <label className='toggle-inactive'>
              <input
                type='checkbox'
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
              Show inactive
            </label>
            <button className='primary-btn' onClick={startNew}>
              + New Use Case
            </button>
          </div>
        </div>

        {error && <div className='msg error'>{error}</div>}

        {listLoading ? (
          <div className='loading'>Loading...</div>
        ) : useCases.length === 0 ? (
          <div className='empty'>No use cases found.</div>
        ) : (
          <div className='use-case-list'>
            {useCases.map((uc) => (
              <div
                key={uc.id}
                className={`use-case-card ${!uc.active ? 'inactive' : ''}`}
                onClick={() => selectUseCase(uc.slug)}
              >
                <div className='card-header'>
                  <span className='card-title'>{uc.title}</span>
                  {!uc.active && (
                    <span className='badge inactive-badge'>Inactive</span>
                  )}
                </div>
                <div className='card-slug'>{uc.slug}</div>
                <div className='card-summary'>{uc.summary}</div>
                <div className='card-meta'>
                  <span className='managers'>
                    {uc.managers.length > 0
                      ? uc.managers.join(', ')
                      : 'No managers'}
                  </span>
                  <span className='expectations'>
                    {uc.expectationCount} expectation
                    {uc.expectationCount !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ─── Render: detail / edit view ─────────────────────────────────────────

  return (
    <div className='use-case-editor'>
      <MobileNavToggle />
      <div className='header'>
        <button className='back-btn' onClick={backToList}>
          &larr; Back
        </button>
        <h2>{isNewMode ? 'New Use Case' : editTitle || 'Edit Use Case'}</h2>
      </div>

      {error && <div className='msg error'>{error}</div>}
      {successMsg && <div className='msg success'>{successMsg}</div>}

      {detailLoading ? (
        <div className='loading'>Loading...</div>
      ) : (
        <div className='edit-form'>
          {/* Slug */}
          <div className='form-group'>
            <label>Slug</label>
            <input
              type='text'
              value={editSlug}
              onChange={(e) => setEditSlug(e.target.value)}
              placeholder='e.g. campaign-creation'
              disabled={!isNewMode}
            />
          </div>

          {/* Active toggle */}
          <div className='form-group inline'>
            <label>
              <input
                type='checkbox'
                checked={editActive}
                onChange={(e) => setEditActive(e.target.checked)}
              />
              Active
            </label>
          </div>

          {/* Title */}
          <div className='form-group'>
            <label>Title</label>
            <input
              type='text'
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              placeholder='Human-readable title'
            />
          </div>

          {/* Summary */}
          <div className='form-group'>
            <label>
              Summary{' '}
              <span className='hint'>
                (injected into system prompt, max ~200 chars)
              </span>
            </label>
            <input
              type='text'
              value={editSummary}
              onChange={(e) => setEditSummary(e.target.value)}
              placeholder='Short summary'
            />
            <span className='char-count'>{editSummary.length}</span>
          </div>

          {/* Description */}
          <div className='form-group'>
            <label>
              Description{' '}
              <span className='hint'>(full instructions for subthread)</span>
            </label>
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder='Full description'
              rows={12}
            />
          </div>

          {/* Limitations */}
          <div className='form-group'>
            <label>Limitations</label>
            <textarea
              value={editLimitations}
              onChange={(e) => setEditLimitations(e.target.value)}
              placeholder='What the agent cannot do (optional)'
              rows={3}
            />
          </div>

          {/* Recommended Modes */}
          <div className='form-group'>
            <label>
              Recommended Modes{' '}
              <span className='hint'>(comma-separated slugs)</span>
            </label>
            <input
              type='text'
              value={editRecommendedModes}
              onChange={(e) => setEditRecommendedModes(e.target.value)}
              placeholder='e.g. email, calendar, planning'
            />
          </div>

          {/* Managers */}
          <div className='form-group'>
            <label>Managers</label>
            <div className='manager-checkboxes'>
              {MANAGER_OPTIONS.map((name) => (
                <label key={name} className='checkbox-label'>
                  <input
                    type='checkbox'
                    checked={editManagers.includes(name)}
                    onChange={() => toggleManager(name)}
                  />
                  {name}
                </label>
              ))}
            </div>
          </div>

          {/* Expectations */}
          <div className='form-group'>
            <label>Expectations</label>
            <div className='expectations-list'>
              {editExpectations.map((exp, i) => (
                <ExpectationRow
                  key={exp.id || `new-${i}`}
                  expectation={exp}
                  index={i}
                  onChange={handleExpectationChange}
                  onRemove={removeExpectation}
                />
              ))}
            </div>
            <button className='secondary-btn' onClick={addExpectation}>
              + Add Expectation
            </button>
          </div>

          {/* Actions */}
          <div className='form-actions'>
            <button
              className='primary-btn'
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : isNewMode ? 'Create' : 'Save Changes'}
            </button>
            {!isNewMode && (
              <button
                className='danger-btn'
                onClick={handleDelete}
                disabled={saving}
              >
                Deactivate
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UseCaseEditor;
